import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiMail, FiSend, FiRefreshCw, FiSearch, FiInbox, FiChevronDown, FiMaximize2, FiMinimize2, FiX, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppHeader from './AppHeader';
import { getValidAccessToken } from '../utils/microsoftAuth';
import { handleSessionExpired } from '../utils/auth';

const EmailInbox = () => {
  // Auth state — re-use tokens stored by EmailComposer
  const [provider, setProvider] = useState(() => localStorage.getItem('active_provider') || 'microsoft');
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem('ms_user_email') || '');
  const [userDisplayName, setUserDisplayName] = useState(() => localStorage.getItem('ms_user_name') || '');

  // Thread list state
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [viewMode, setViewMode] = useState('threads'); // 'threads' | 'messages' | 'sent'
  const [messages, setMessages] = useState([]);
  const [messagesNextPageToken, setMessagesNextPageToken] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesBackgroundLoading, setMessagesBackgroundLoading] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [recallingId, setRecallingId] = useState(null);

  // Selected thread / conversation state
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);

  // Reply state
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [showReplyConfirm, setShowReplyConfirm] = useState(false);
  const [pendingReplyQueueId, setPendingReplyQueueId] = useState(null);
  const [pendingReplyRecalled, setPendingReplyRecalled] = useState(false);
  const [replyCountdown, setReplyCountdown] = useState(60);

  // UI state
  const [isFullscreen, setIsFullscreen] = useState(false);

  const messagesEndRef = useRef(null);
  const replyInFlightRef = useRef(false);

  // Refs for latest state values used by background loader (avoid stale closures)
  const viewModeRef = useRef(viewMode);
  const nextPageTokenRef = useRef(nextPageToken);
  const messagesNextPageTokenRef = useRef(messagesNextPageToken);
  const loadingRef = useRef(loading);
  const messagesLoadingRef = useRef(messagesLoading);
  const syncingRef = useRef(syncing);
  const messagesBackgroundLoadingRef = useRef(messagesBackgroundLoading);
  // Load callbacks are defined later in this scope, so initialize refs to no-op and update after declaration
  const loadThreadsRef = useRef(() => {});
  const loadMessagesRef = useRef(() => {});

  useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);
  useEffect(() => { nextPageTokenRef.current = nextPageToken; }, [nextPageToken]);
  useEffect(() => { messagesNextPageTokenRef.current = messagesNextPageToken; }, [messagesNextPageToken]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { messagesLoadingRef.current = messagesLoading; }, [messagesLoading]);
  useEffect(() => { syncingRef.current = syncing; }, [syncing]);
  useEffect(() => { messagesBackgroundLoadingRef.current = messagesBackgroundLoading; }, [messagesBackgroundLoading]);

  const getAccessToken = useCallback(() => {
    if (provider === 'gmail') return localStorage.getItem('google_access_token') || '';
    return localStorage.getItem('ms_access_token') || '';
  }, [provider]);

  // Async version: refreshes the Microsoft token if it's about to expire.
  // Gmail tokens are returned as-is (no refresh flow implemented for Google).
  const authHeaders = useCallback(async () => {
    if (provider === 'gmail') {
      const token = localStorage.getItem('google_access_token') || '';
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const token = await getValidAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [provider]);

  const hasToken = !!getAccessToken();

  // ── Load threads ─────────────────────────────────────────────────────
  const loadThreads = useCallback(async (append = false, background = false) => {
    const token = getAccessToken();
    if (!token) return;
    if (background) setSyncing(true); else setLoading(true);
    try {
      const params = { provider, maxResults: 20 };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (append && nextPageToken) params.pageToken = nextPageToken;

      const { data } = await axios.get('/api/inbox/threads', {
        headers: await authHeaders(),
        params,
      });
      if (append) {
        setThreads((prev) => [...prev, ...(data.threads || [])]);
      } else {
        setThreads(data.threads || []);
      }
      setNextPageToken(data.nextPageToken || null);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      if (e?.response?.status === 401) {
        handleSessionExpired('Your email session has expired. Please sign in again.');
      } else {
        toast.error(`Failed to load inbox: ${msg}`);
      }
      console.error('loadThreads error:', e);
    } finally {
      if (background) setSyncing(false); else setLoading(false);
    }
  }, [provider, searchQuery, nextPageToken, authHeaders, getAccessToken]);

  // ── Load messages (paginated) ────────────────────────────────────────
  const loadMessages = useCallback(async (append = false, background = false) => {
    const token = getAccessToken();
    if (!token) return;
    if (background) setMessagesBackgroundLoading(true); else setMessagesLoading(true);
    try {
      const params = { provider, maxResults: 20 };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (append && messagesNextPageToken) params.pageToken = messagesNextPageToken;

      const { data } = await axios.get('/api/inbox/messages/all', {
        headers: await authHeaders(),
        params,
      });
      if (append) {
        setMessages((prev) => [...prev, ...(data.messages || [])]);
      } else {
        setMessages(data.messages || []);
      }
      setMessagesNextPageToken(data.nextPageToken || null);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      if (e?.response?.status === 401) {
        handleSessionExpired('Your email session has expired. Please sign in again.');
      } else {
        toast.error(`Failed to load messages: ${msg}`);
      }
      console.error('loadMessages error:', e);
    } finally {
      if (background) setMessagesBackgroundLoading(false); else setMessagesLoading(false);
    }
  }, [provider, searchQuery, messagesNextPageToken, authHeaders, getAccessToken]);

  // Update refs now that the callbacks are defined
  useEffect(() => { loadThreadsRef.current = loadThreads; }, [loadThreads]);
  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);

  // Auto-load on mount / provider change — only the current view, fast
  useEffect(() => {
    if (!hasToken) return;
    if (viewMode === 'threads') {
      loadThreads(false);
    } else if (viewMode === 'messages') {
      loadMessages(false);
    } else if (viewMode === 'sent') {
      loadQueueItems();
    }
  }, [provider, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background load: keep fetching next pages while the user is on this view.
  // Uses refs so the loop always sees the most recent tokens/flags without restarting.
  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;

    const loadMore = async () => {
      const mode = viewModeRef.current;
      if (mode === 'threads' && nextPageTokenRef.current && !loadingRef.current && !syncingRef.current) {
        await loadThreadsRef.current(true, true);
      } else if (mode === 'messages' && messagesNextPageTokenRef.current && !messagesLoadingRef.current && !messagesBackgroundLoadingRef.current) {
        await loadMessagesRef.current(true, true);
      }
    };

    const run = async () => {
      while (!cancelled) {
        const mode = viewModeRef.current;
        const hasMore =
          (mode === 'threads' && nextPageTokenRef.current) ||
          (mode === 'messages' && messagesNextPageTokenRef.current);
        if (!hasMore) break;
        await loadMore();
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
    };

    run();
    return () => { cancelled = true; };
  }, [provider, hasToken]);

  // ── Open thread ──────────────────────────────────────────────────────
  const openThread = async (thread) => {
    setSelectedThread(thread);
    setThreadMessages([]);
    setReplyBody('');
    setLoadingThread(true);
    try {
      const { data } = await axios.get(`/api/inbox/thread/${encodeURIComponent(thread.id)}`, {
        headers: await authHeaders(),
        params: { provider },
      });
      setThreadMessages(data.messages || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      toast.error(`Failed to load conversation: ${msg}`);
      console.error('openThread error:', e);
    } finally {
      setLoadingThread(false);
    }
  };

  // ── Send reply (double confirmation + queue + recall) ───────────────
  const handleSendReply = (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !selectedThread || threadMessages.length === 0) return;
    setShowReplyConfirm(true);
  };

  const handleConfirmReply = async () => {
    if (replyInFlightRef.current) return;
    replyInFlightRef.current = true;
    setShowReplyConfirm(false);
    if (!replyBody.trim() || !selectedThread || threadMessages.length === 0) {
      replyInFlightRef.current = false;
      return;
    }
    const lastMsg = threadMessages[threadMessages.length - 1];
    const replyText = replyBody.trim();
    setSending(true);
    try {
      const token = provider === 'gmail' ? getAccessToken() : (await getValidAccessToken() || '');
      if (!token) {
        toast.error('Session expired. Please sign in again from the Email page.');
        replyInFlightRef.current = false;
        return;
      }
      const sendAfter = new Date(Date.now() + 60 * 1000).toISOString();
      const { data } = await axios.post(
        '/api/scheduler/queue',
        {
          from_email: userEmail,
          recipients: [extractEmail(lastMsg.from)],
          subject: selectedThread.subject,
          body: replyText,
          provider,
          access_token: token,
          send_after: sendAfter,
          reply_to_message_id: lastMsg.id,
          reply_to_thread_id: selectedThread.id,
        },
        { headers: await authHeaders() }
      );
      const queueId = data.id;
      setPendingReplyQueueId(queueId);
      setPendingReplyRecalled(false);
      setReplyBody('');
      toast.success('Reply queued — you can recall it for 60 seconds.');

      // Show pending reply in the thread (replace any existing pending for this text)
      const pendingMsg = {
        id: `pending-${queueId}`,
        from: userEmail,
        to: extractEmail(lastMsg.from),
        date: new Date().toISOString(),
        body: `<p>${replyText.replace(/\n/g, '<br/>')}</p>`,
        pending: true,
        queueId,
      };
      setThreadMessages((prev) => {
        const filtered = prev.filter((m) => !(m.pending && m.body === pendingMsg.body));
        return [...filtered, pendingMsg];
      });
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      toast.error(`Failed to queue reply: ${msg}`);
      console.error('queueReply error:', e);
    } finally {
      setSending(false);
      replyInFlightRef.current = false;
    }
  };

  const handleRecallPendingReply = async () => {
    if (!pendingReplyQueueId) return;
    setRecallingId(pendingReplyQueueId);
    try {
      await axios.post(`/api/scheduler/queue/${pendingReplyQueueId}/recall`);
      setPendingReplyRecalled(true);
      toast.success('Reply recalled successfully');
      setThreadMessages((prev) => prev.filter((m) => m.queueId !== pendingReplyQueueId));
      setPendingReplyQueueId(null);
    } catch (e) {
      toast.error('Could not recall reply: ' + (e.response?.data?.error || e.message));
    } finally {
      setRecallingId(null);
    }
  };

  // ── Search handler ───────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    if (viewMode === 'messages') {
      loadMessages(false);
    } else {
      loadThreads(false);
    }
  };

  // ── Recent sends / recall ────────────────────────────────────────────
  const loadQueueItems = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/scheduler/queue?limit=20', { headers: await authHeaders() });
      setQueueItems(data.items || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        handleSessionExpired('Your email session has expired. Please sign in again.');
      } else {
        console.error('loadQueueItems error:', e);
      }
    }
  }, [authHeaders]);

  const handleRecallFromInbox = async (id) => {
    setRecallingId(id);
    try {
      await axios.post(`/api/scheduler/queue/${id}/recall`);
      toast.success('Email recalled successfully');
      loadQueueItems();
    } catch (e) {
      toast.error('Could not recall: ' + (e.response?.data?.error || e.message));
    } finally {
      setRecallingId(null);
    }
  };

  // Poll recent sends every 3 seconds while on this page
  useEffect(() => {
    loadQueueItems();
    const interval = setInterval(loadQueueItems, 3000);
    return () => clearInterval(interval);
  }, [loadQueueItems]);

  // Refresh queue list immediately when user opens Sent / Recall tab
  useEffect(() => {
    if (viewMode === 'sent') loadQueueItems();
  }, [viewMode, loadQueueItems]);

  const queueTimeLeft = (sendAfter) => {
    const diff = Math.ceil((new Date(sendAfter) - Date.now()) / 1000);
    return diff > 0 ? `${diff}s` : 'sending now';
  };

  // Live countdown for pending reply recall window
  useEffect(() => {
    if (!pendingReplyQueueId || pendingReplyRecalled) {
      setReplyCountdown(60);
      return;
    }
    setReplyCountdown(60);
    const interval = setInterval(() => {
      setReplyCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [pendingReplyQueueId, pendingReplyRecalled]);

  // Watch pending reply and refresh thread once it is actually sent
  useEffect(() => {
    if (!pendingReplyQueueId || pendingReplyRecalled) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`/api/scheduler/queue/${pendingReplyQueueId}`, { headers: await authHeaders() });
        if (data.status === 'sent' || data.status === 'failed') {
          setPendingReplyQueueId(null);
          setThreadMessages((prev) => prev.filter((m) => m.queueId !== pendingReplyQueueId));
          if (selectedThread) openThread(selectedThread);
        }
      } catch (e) {
        console.error('pending reply poll error:', e);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [pendingReplyQueueId, pendingReplyRecalled, selectedThread, authHeaders, openThread]);

  // ── Sync all individual messages (Outlook-style list) ────────────────
  const handleSyncMessages = async () => {
    const token = getAccessToken();
    if (!token) return;
    setSyncing(true);
    setMessagesLoading(true);
    setSyncProgress('Syncing messages…');
    let allMessages = [];
    let pageToken = null;
    let page = 0;
    try {
      do {
        console.log(`[inbox] syncMessages requesting page ${page + 1}, pageToken=${pageToken ? 'yes' : 'no'}`);
        const { data } = await axios.get('/api/inbox/messages/all', {
          headers: await authHeaders(),
          params: { provider, q: searchQuery.trim() || undefined, maxResults: 100, pageToken: pageToken || undefined },
        });
        const batch = data.messages || [];
        allMessages = [...allMessages, ...batch];
        pageToken = data.nextPageToken || null;
        page += 1;
        console.log(`[inbox] syncMessages page ${page} returned ${batch.length} messages, nextPageToken=${pageToken ? 'yes' : 'no'}, complete=${data.complete}`);
        setSyncProgress(`Synced ${allMessages.length.toLocaleString()} messages…`);
        setMessages([...allMessages]);
      } while (pageToken && page < 200);
      setMessagesNextPageToken(pageToken);
      setSyncProgress(`Synced ${allMessages.length.toLocaleString()} messages · ${pageToken ? 'partial' : 'complete'}`);
      toast.success(`Inbox sync complete: ${allMessages.length.toLocaleString()} messages`);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      if (e?.response?.status === 401) {
        toast.error('Session expired. Please sign in again from the Email page.');
      } else {
        toast.error(`Sync failed: ${msg}`);
      }
      console.error('handleSyncMessages error:', e);
    } finally {
      setSyncing(false);
      setMessagesLoading(false);
    }
  };

  // ── Sync all threads (fetch every page) ─────────────────────────────
  const handleSyncAll = async () => {
    const token = getAccessToken();
    if (!token) return;
    setSyncing(true);
    setSyncProgress('Starting sync…');
    try {
      const { data } = await axios.get('/api/inbox/threads/all', {
        headers: authHeaders(),
        params: { provider, q: searchQuery.trim() || undefined },
      });
      setThreads(data.threads || []);
      setNextPageToken(null);
      setSyncProgress(`Synced ${(data.threads || []).length} threads · ${data.complete ? 'complete' : 'partial'}`);
      toast.success(`Inbox sync complete: ${(data.threads || []).length} threads`);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      if (e?.response?.status === 401) {
        toast.error('Session expired. Please sign in again from the Email page.');
      } else {
        toast.error(`Sync failed: ${msg}`);
      }
      console.error('handleSyncAll error:', e);
    } finally {
      setSyncing(false);
    }
  };

  // ── Extract email address from "Name <email>" format ─────────────────
  const extractEmail = (str) => {
    const match = (str || '').match(/<([^>]+)>/);
    return match ? match[1] : str;
  };

  const extractName = (str) => {
    const match = (str || '').match(/^(.+?)\s*</);
    return match ? match[1].trim().replace(/^["']|["']$/g, '') : extractEmail(str);
  };

  // ── Format date ──────────────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffH = diffMs / 3600000;
    if (diffH < 1) return `${Math.max(1, Math.floor(diffMs / 60000))}m ago`;
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    if (diffH < 48) return 'Yesterday';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  // ── Not signed in ────────────────────────────────────────────────────
  if (!hasToken) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-neutral-200 p-8 text-center max-w-md">
          <FiInbox className="w-12 h-12 mx-auto text-primary-500 mb-4" />
          <h1 className="text-xl font-bold text-neutral-900 mb-2">Email Inbox</h1>
          <p className="text-neutral-600 mb-6">
            Sign in on the <a href="/" className="text-primary-600 underline font-medium">Email Composer</a> page first, then come back here to view your inbox.
          </p>
          <a href="/" className="inline-block px-6 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition">
            Go to Email Composer
          </a>
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────
  return (
    <div className={isFullscreen
      ? 'fixed inset-0 z-50 bg-neutral-50 flex flex-col'
      : 'min-h-screen bg-neutral-50'
    }>
      <AppHeader />

      {/* Page toolbar */}
      <div className="bg-white border-b border-neutral-200 px-4 py-2">
        <div className={`mx-auto flex items-center justify-between gap-4 flex-wrap ${isFullscreen ? 'max-w-full px-4' : 'max-w-7xl'}`}>
          <div className="flex items-center gap-3">
            <FiInbox className="w-5 h-5 text-primary-600" />
            <h1 className="text-lg font-bold text-neutral-900">Email Inbox</h1>
            <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">
              {provider === 'gmail' ? 'Gmail' : 'Microsoft'} · {userDisplayName || userEmail}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value);
                setSelectedThread(null);
                setThreads([]);
              }}
              className="border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
            >
              <option value="microsoft">Microsoft</option>
              <option value="gmail">Gmail</option>
            </select>
            <button
              onClick={() => setIsFullscreen((v) => !v)}
              className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
              title={isFullscreen ? 'Exit full screen' : 'Full screen'}
            >
              {isFullscreen ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className={`flex flex-1 min-h-0 ${isFullscreen ? '' : 'max-w-7xl mx-auto w-full'}`}>
        {/* ── Thread list panel ──────────────────────────────────────── */}
        <div className={`${selectedThread ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-96 border-r border-neutral-200 bg-white ${isFullscreen ? 'h-full' : 'min-h-[calc(100vh-60px)]'}`}>
          {/* Search bar */}
          <div className="p-3 border-b border-neutral-200">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search emails..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (viewMode === 'messages') loadMessages(false);
                  else loadThreads(false);
                }}
                disabled={loading || syncing || messagesLoading}
                className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 disabled:opacity-50"
                title="Refresh"
              >
                <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={viewMode === 'threads' ? handleSyncAll : viewMode === 'messages' ? handleSyncMessages : loadQueueItems}
                disabled={syncing && viewMode !== 'sent'}
                className="px-3 py-2 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5"
                title="Fetch all inbox pages"
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${syncing && viewMode !== 'sent' ? 'animate-spin' : ''}`} />
                {viewMode === 'sent' ? (syncing ? 'Refreshing…' : 'Refresh') : syncing ? 'Syncing…' : 'Sync all'}
              </button>
            </form>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setViewMode('threads')}
                className={`px-3 py-1 rounded-md text-xs font-medium ${viewMode === 'threads' ? 'bg-primary-100 text-primary-700' : 'text-neutral-500 hover:bg-neutral-100'}`}
              >
                Threads
              </button>
              <button
                type="button"
                onClick={() => setViewMode('messages')}
                className={`px-3 py-1 rounded-md text-xs font-medium ${viewMode === 'messages' ? 'bg-primary-100 text-primary-700' : 'text-neutral-500 hover:bg-neutral-100'}`}
              >
                All Messages
              </button>
              <button
                type="button"
                onClick={() => setViewMode('sent')}
                className={`px-3 py-1 rounded-md text-xs font-medium ${viewMode === 'sent' ? 'bg-red-100 text-red-700' : 'text-neutral-500 hover:bg-neutral-100'}`}
              >
                Sent / Recall
              </button>
              <span className="text-xs text-neutral-400 ml-auto">
                {viewMode === 'threads' ? `${threads.length.toLocaleString()} threads` : viewMode === 'messages' ? `${messages.length.toLocaleString()} messages` : `${queueItems.length.toLocaleString()} items`}
              </span>
            </div>
          </div>
          {syncProgress && (
            <div className="px-3 py-1.5 text-xs text-primary-700 bg-primary-50 border-b border-primary-100">
              {syncProgress}
            </div>
          )}

          {/* Thread / Message / Sent list */}
          <div className="flex-1 overflow-y-auto">
            {viewMode === 'sent' && (
              <div className="p-3">
                {queueItems.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-40 text-neutral-400 text-sm">
                    <FiMail className="w-8 h-8 mb-2" />
                    No recent sends
                  </div>
                )}
                <div className="space-y-2">
                  {[...new Map(queueItems.map((i) => [i.id, i])).values()].map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        if (item.reply_to_thread_id) {
                          openThread({ id: item.reply_to_thread_id, subject: item.subject });
                        } else {
                          toast('This send has no conversation thread.');
                        }
                      }}
                      className={`rounded-lg border p-3 text-sm cursor-pointer hover:shadow-sm transition ${item.status === 'recalled' ? 'bg-red-50 border-red-100' : item.status === 'failed' ? 'bg-orange-50 border-orange-100' : item.status === 'sent' ? 'bg-green-50 border-green-100' : 'bg-white border-neutral-200 hover:bg-neutral-50'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-neutral-800 truncate">{item.subject}</span>
                        <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                          item.status === 'queued' ? 'bg-amber-100 text-amber-700' :
                          item.status === 'sending' ? 'bg-blue-100 text-blue-700' :
                          item.status === 'sent' ? 'bg-green-100 text-green-700' :
                          item.status === 'recalled' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>{item.status}</span>
                      </div>
                      <div className="text-xs text-neutral-500 truncate mt-1">To: {(item.recipients || []).join(', ')}</div>
                      <div className="text-xs text-neutral-400 mt-1">
                        {formatDate(item.createdAt)}
                        {item.status === 'queued' && item.send_after && (
                          <span className="ml-2 text-amber-600 font-medium">· sending in {queueTimeLeft(item.send_after)}</span>
                        )}
                      </div>
                      {(item.status === 'queued' || item.status === 'sending') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRecallFromInbox(item.id); }}
                          disabled={recallingId === item.id}
                          className="mt-2 px-2.5 py-1 bg-red-600 text-white text-xs font-semibold rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {recallingId === item.id ? <FiRefreshCw className="w-3 h-3 animate-spin" /> : <FiX className="w-3 h-3" />}
                          Recall
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {viewMode === 'threads' && loading && threads.length === 0 && (
              <div className="flex items-center justify-center h-40 text-neutral-500 text-sm">
                <FiRefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
              </div>
            )}
            {viewMode === 'messages' && messagesLoading && messages.length === 0 && (
              <div className="flex items-center justify-center h-40 text-neutral-500 text-sm">
                <FiRefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading messages…
              </div>
            )}
            {viewMode === 'threads' && !loading && threads.length === 0 && (
              <div className="flex flex-col items-center justify-center h-40 text-neutral-400 text-sm">
                <FiMail className="w-8 h-8 mb-2" />
                No threads found
              </div>
            )}
            {viewMode === 'messages' && !messagesLoading && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-40 text-neutral-400 text-sm">
                <FiMail className="w-8 h-8 mb-2" />
                No messages found
              </div>
            )}
            {viewMode === 'threads' && threads.map((t) => (
              <button
                key={t.id}
                onClick={() => openThread(t)}
                className={`w-full text-left px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition ${
                  selectedThread?.id === t.id ? 'bg-primary-50 border-l-2 border-l-primary-500' : ''
                } ${t.unread ? 'bg-blue-50/50' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${t.unread ? 'font-bold text-neutral-900' : 'font-medium text-neutral-700'}`}>
                    {extractName(t.lastFrom || t.from)}
                  </span>
                  <span className="text-xs text-neutral-400 shrink-0">{formatDate(t.date)}</span>
                </div>
                <div className={`text-sm truncate mt-0.5 ${t.unread ? 'font-semibold text-neutral-800' : 'text-neutral-600'}`}>
                  {t.subject}
                </div>
                <div className="text-xs text-neutral-400 truncate mt-0.5">{t.snippet}</div>
                {t.messageCount > 1 && (
                  <span className="inline-block mt-1 text-[10px] bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded-full">
                    {t.messageCount} messages
                  </span>
                )}
              </button>
            ))}
            {viewMode === 'messages' && messages.map((m) => (
              <button
                key={m.id}
                onClick={() => openThread({ id: m.threadId, subject: m.subject })}
                className={`w-full text-left px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition ${
                  selectedThread?.id === m.threadId ? 'bg-primary-50 border-l-2 border-l-primary-500' : ''
                } ${m.unread ? 'bg-blue-50/50' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${m.unread ? 'font-bold text-neutral-900' : 'font-medium text-neutral-700'}`}>
                    {extractName(m.from)}
                  </span>
                  <span className="text-xs text-neutral-400 shrink-0">{formatDate(m.date)}</span>
                </div>
                <div className={`text-sm truncate mt-0.5 ${m.unread ? 'font-semibold text-neutral-800' : 'text-neutral-600'}`}>
                  {m.subject}
                </div>
                <div className="text-xs text-neutral-400 truncate mt-0.5">{m.snippet}</div>
              </button>
            ))}
            {viewMode === 'threads' && nextPageToken && (
              <button
                onClick={() => loadThreads(true)}
                disabled={loading}
                className="w-full py-3 text-sm text-primary-600 hover:bg-neutral-50 flex items-center justify-center gap-1"
              >
                <FiChevronDown className="w-4 h-4" /> Load more
              </button>
            )}
            {viewMode === 'messages' && messagesNextPageToken && (
              <button
                onClick={() => loadMessages(true)}
                disabled={messagesLoading}
                className="w-full py-3 text-sm text-primary-600 hover:bg-neutral-50 flex items-center justify-center gap-1"
              >
                <FiChevronDown className="w-4 h-4" /> Load more
              </button>
            )}
            {((viewMode === 'threads' && syncing) || (viewMode === 'messages' && messagesBackgroundLoading)) && (
              <div className="py-2 text-xs text-neutral-500 flex items-center justify-center gap-1.5">
                <FiRefreshCw className="w-3 h-3 animate-spin" /> Loading more…
              </div>
            )}
          </div>

          {/* Recent Sends / Recall panel */}
          {[...new Map(queueItems.map((i) => [i.id, i])).values()].some((item) => item.status === 'queued' || item.status === 'sending') && (
            <div className="border-t border-neutral-200 bg-red-50/60 p-3">
              <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-red-700">
                <FiSend className="w-3.5 h-3.5" /> Recent Sends — Recall
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {[...new Map(queueItems.map((i) => [i.id, i])).values()]
                  .filter((item) => item.status === 'queued' || item.status === 'sending')
                  .slice(0, 5)
                  .map((item) => (
                    <div key={item.id} className="bg-white rounded-lg border border-red-100 p-2 text-xs">
                      <div className="truncate font-medium text-neutral-800">{item.subject}</div>
                      <div className="text-neutral-500 truncate">{(item.recipients || []).join(', ')}</div>
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">{item.status}</span>
                        <button
                          onClick={() => handleRecallFromInbox(item.id)}
                          disabled={recallingId === item.id}
                          className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-[10px] font-semibold flex items-center gap-1"
                        >
                          {recallingId === item.id ? <FiRefreshCw className="w-3 h-3 animate-spin" /> : <FiX className="w-3 h-3" />}
                          Recall
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Conversation panel ─────────────────────────────────────── */}
        <div className={`${selectedThread ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-white ${isFullscreen ? 'h-full' : 'min-h-[calc(100vh-60px)]'}`}>
          {!selectedThread ? (
            <div className="flex-1 flex items-center justify-center text-neutral-400">
              <div className="text-center">
                <FiMail className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Select a conversation to view</p>
              </div>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-3">
                <button
                  onClick={() => setSelectedThread(null)}
                  className="md:hidden p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100"
                >
                  <FiArrowLeft className="w-4 h-4" />
                </button>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-bold text-neutral-900 truncate">{selectedThread.subject}</h2>
                  <p className="text-xs text-neutral-500 truncate">{threadMessages.length} message{threadMessages.length !== 1 ? 's' : ''}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {loadingThread && (
                  <div className="flex items-center justify-center h-20 text-neutral-500 text-sm">
                    <FiRefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
                  </div>
                )}
                {threadMessages
                  .filter((msg, idx, arr) => {
                    // Hide visual duplicates (same from/to/body) keeping the first occurrence
                    const key = `${extractEmail(msg.from)}|${msg.to || ''}|${msg.body}`;
                    return arr.findIndex((m) => `${extractEmail(m.from)}|${m.to || ''}|${m.body}` === key) === idx;
                  })
                  .map((msg) => {
                  const fromEmail = extractEmail(msg.from);
                  const isMine = fromEmail.toLowerCase() === userEmail.toLowerCase();
                  const isPending = msg.pending;
                  return (
                    <div
                      key={msg.id}
                      className={`rounded-xl border p-4 ${
                        isPending
                          ? 'bg-amber-50 border-amber-200 ml-4 md:ml-16'
                          : isMine
                          ? 'bg-primary-50 border-primary-200 ml-4 md:ml-16'
                          : 'bg-white border-neutral-200 mr-4 md:mr-16'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-sm font-semibold text-neutral-800">
                          {isMine ? 'You' : extractName(msg.from)}
                          {isPending && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                              Pending
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-neutral-400 shrink-0">{formatFullDate(msg.date)}</span>
                      </div>
                      {msg.to && (
                        <div className="text-xs text-neutral-400 mb-2">
                          To: {msg.to}
                        </div>
                      )}
                      <div
                        className="prose prose-sm max-w-none text-neutral-700 overflow-x-auto"
                        dangerouslySetInnerHTML={{ __html: msg.body }}
                      />
                      {isPending && (
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="text-xs text-amber-600 font-medium">
                            ⏳ Sending in {replyCountdown}s — recall available
                          </span>
                          <button
                            onClick={handleRecallPendingReply}
                            disabled={recallingId === msg.queueId}
                            className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 w-fit"
                          >
                            {recallingId === msg.queueId ? <FiRefreshCw className="w-3 h-3 animate-spin" /> : <FiX className="w-3 h-3" />}
                            Recall reply
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply box */}
              <div className="border-t border-neutral-200 p-3 bg-neutral-50">
                <form onSubmit={handleSendReply} className="flex gap-2">
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1 resize-none border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                  <button
                    type="submit"
                    disabled={sending || !replyBody.trim()}
                    className="self-end px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition"
                  >
                    {sending ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
                    Send
                  </button>
                </form>
              </div>

              {/* Reply double-confirmation modal */}
              {showReplyConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                    <h3 className="text-lg font-bold text-neutral-900 mb-2">Confirm Reply</h3>
                    <p className="text-sm text-neutral-600 mb-4">
                      You have <strong>60 seconds</strong> to recall this reply after confirming.
                    </p>
                    <div className="bg-neutral-50 rounded-lg p-3 text-sm text-neutral-800 mb-6 max-h-40 overflow-y-auto">
                      {replyBody.trim()}
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setShowReplyConfirm(false)}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-100"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmReply}
                        disabled={sending}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {sending ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
                        Yes, Send Reply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailInbox;
